import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useHistory } from 'react-router-dom';
import { api } from '../services/api';
import { getStoredItem, removeStoredItem, setStoredItem } from '../utils/storage';
import { buildPurchaseConfirmation, CHECKOUT_IN_PROGRESS_KEY, formatCadence, PURCHASE_CONFIRMATION_KEY } from '../utils/subscriptionPurchase';

const formatDate = (value) => {
  if (!value) return '—';
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return '—';
  return date.toLocaleDateString(undefined, { timeZone: 'UTC' });
};

const formatAccessThroughDate = (value) => {
  if (!value) return '—';
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return '—';
  if (
    date.getUTCHours() === 0 &&
    date.getUTCMinutes() === 0 &&
    date.getUTCSeconds() === 0 &&
    date.getUTCMilliseconds() === 0
  ) {
    date.setUTCDate(date.getUTCDate() - 1);
  }
  return formatDate(date);
};

const formatApiLimit = (value) => {
  if (value === null || value === undefined) return 'Unlimited';
  const parsed = Number(value);
  if (Number.isNaN(parsed)) return String(value);
  return parsed.toLocaleString();
};

const formatHistory = (value) => {
  if (value === null || value === undefined) return '—';
  const parsed = Number(value);
  if (Number.isNaN(parsed)) return String(value);
  if (parsed === 999) return 'All Historic Data';
  if (parsed === 0) return 'Latest Year Only';
  if (parsed === 1) return '1 Year Back';
  return `${parsed} Years Back`;
};

const formatMoney = (cents) => {
  if (cents === null || cents === undefined) return '—';
  return `$${(Number(cents) / 100).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
};

const formatCurrencyCode = (value) => String(value || 'usd').toUpperCase();

const getAnnualSavingsCents = (plan) => {
  const monthly = Number(plan?.price_monthly_cents || 0);
  const annual = Number(plan?.price_annual_cents || 0);
  const savings = (monthly * 12) - annual;
  return savings > 0 ? savings : 0;
};

const PLAN_RANK = {
  trial: 0,
  starter: 1,
  growth: 2,
  enterprise: 3,
};

const getActivePriceLabel = (subscription) => {
  if (!subscription?.billing_cadence) return 'Current Price';
  return subscription.billing_cadence === 'annual' ? 'Annual Price' : 'Monthly Price';
};

const getActivePriceValue = (subscription) => {
  if (!subscription) return '—';
  if (subscription.billing_cadence === 'annual') return formatMoney(subscription.price_annual_cents);
  return formatMoney(subscription.price_monthly_cents);
};

const getGrandfatherSunsetPriceLabel = (subscription) => {
  if (!subscription?.grandfather_sunset?.billing_cadence) return 'Upcoming Price';
  return subscription.grandfather_sunset.billing_cadence === 'annual' ? 'Annual price' : 'Monthly price';
};

const formatEntitlementChangeValue = (key, value) => {
  if (key === 'api_rate_limit') return `${formatApiLimit(value)} per month`;
  if (key === 'api_src_limit') return value || 'All Data Sources';
  if (key === 'api_src_derived_flag' || key === 'excel_plugin_flag') return value ? 'Included' : 'Not Included';
  if (key === 'api_hist_limit') return formatHistory(value);
  return value ?? '—';
};

const CUSTOMER_VISIBLE_ENTITLEMENT_KEYS = new Set([
  'api_rate_limit',
  'api_src_limit',
  'api_src_derived_flag',
  'api_hist_limit',
]);

const getCustomerVisibleEntitlementChanges = (changes) => (
  Array.isArray(changes)
    ? changes.filter((change) => CUSTOMER_VISIBLE_ENTITLEMENT_KEYS.has(change?.key))
    : []
);

const formatAccountFeatureValue = (value) => (value ? 'Included' : 'Not Included');
const getSsoAccountFeature = (source) => Boolean(
  source?.account_features?.sso_custom_idp
  || source?.sso_custom_idp_flag
);

const IncludedValue = ({ included }) => (
  <span className={included ? 'text-success fw-semibold' : ''}>
    <i className={`fas ${included ? 'fa-check-circle text-success' : 'fa-minus-circle text-muted'} me-1`} aria-hidden="true"></i>
    {included ? 'Included' : 'Not Included'}
  </span>
);

const PlanFeatureRow = ({ label, children, className = '' }) => (
  <div className={`small mb-1 ${className}`.trim()}>
    <span className="text-muted">{label}:</span>{' '}
    <span className="fw-semibold">{children}</span>
  </div>
);

const formatBillingAddress = (data) => {
  const line1 = [data?.billing_addr_street, data?.billing_addr_street_2].filter(Boolean).join(', ');
  const country = data?.billing_addr_country || ((data?.billing_addr_street || data?.billing_addr_city || data?.billing_addr_state || data?.billing_addr_zip) ? 'US' : '');
  const locality = [data?.billing_addr_city, data?.billing_addr_state, data?.billing_addr_zip, country].filter(Boolean).join(', ');
  const parts = [line1, locality].filter(Boolean);
  return parts.length > 0 ? parts.join(', ') : '—';
};

const formatDateRange = (start, end) => {
  if (!start && !end) return null;
  const startLabel = start ? formatDate(start) : '—';
  const endLabel = end ? formatDate(end) : '—';
  return `${startLabel} - ${endLabel}`;
};

const SUBSCRIPTION_NOTICE_KEY = 'finbase.subscription_notice';

const EntitlementChangesTable = ({ changes }) => (
  <div className="table-responsive w-100">
    <table className="table table-sm align-middle mb-0">
      <thead>
        <tr>
          <th>Entitlement</th>
          <th>Current Limit</th>
          <th>New Limit</th>
        </tr>
      </thead>
      <tbody>
        {changes.map((change) => (
          <tr key={change.key}>
            <td>{change.label}</td>
            <td>{formatEntitlementChangeValue(change.key, change.current)}</td>
            <td>{formatEntitlementChangeValue(change.key, change.target)}</td>
          </tr>
        ))}
      </tbody>
    </table>
  </div>
);

const AccountFeatureChanges = ({ changes, effectiveLabel }) => {
  const visibleChanges = Array.isArray(changes)
    ? changes.filter((change) => change?.key === 'sso_custom_idp')
    : [];
  if (visibleChanges.length === 0) return null;
  return (
    <div className="small px-3 py-2 mt-3 rounded border-start border-3 border-info bg-light">
      <div className="fw-semibold mb-1">Account Features Update</div>
      {effectiveLabel && <div>{effectiveLabel}</div>}
      {visibleChanges.map((change) => (
        <div key={change.key}>
          {change.target
            ? 'SSO / Custom IdP will be included when this change takes effect.'
            : 'SSO / Custom IdP will be disabled when this change takes effect.'}
        </div>
      ))}
    </div>
  );
};

const shouldShowTrialEnd = (subscription) => subscription?.status === 'trialing';
const shouldShowNextRenewal = (subscription) => ['active', 'scheduled_downgrade'].includes(subscription?.raw_status || subscription?.status);
const getManageCardTitle = (subscription, canStartCheckout, canChangePaidSubscription) => {
  if (canChangePaidSubscription) return 'Manage Subscription';
  if (canStartCheckout) return 'Start Paid Subscription';
  return 'Subscription Plans';
};
const getSubscriptionChangeMode = (subscription, planCode, cadence) => {
  if (!subscription?.plan_code || !subscription?.billing_cadence) return 'none';
  if (subscription.plan_code === planCode && subscription.billing_cadence === cadence) return 'none';
  if (subscription.billing_cadence === 'annual' && cadence === 'monthly') return 'scheduled';
  const currentRank = PLAN_RANK[subscription.plan_code] || 0;
  const targetRank = PLAN_RANK[planCode] || 0;
  if (targetRank > currentRank) return 'immediate';
  if (targetRank < currentRank) return 'scheduled';
  if (subscription.billing_cadence === 'monthly' && cadence === 'annual') return 'immediate';
  return 'scheduled';
};
const getDetailedChangeButtonLabel = ({ subscription, planCode, cadence }) => {
  if (!subscription?.plan_code || !subscription?.billing_cadence) return `Choose ${formatCadence(cadence)} Billing`;
  if (subscription.plan_code === planCode && subscription.billing_cadence === cadence) {
    return `${formatCadence(cadence)} Billing (Current)`;
  }
  return `Switch to ${planCode.charAt(0).toUpperCase() + planCode.slice(1)} (${formatCadence(cadence)} Billing)`;
};

const Subscription = ({ onUpdated = null }) => {
  const history = useHistory();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [notice, setNotice] = useState('');
  const [noticeAction, setNoticeAction] = useState(null);
  const [actionLoading, setActionLoading] = useState('');
  const [changePreview, setChangePreview] = useState(null);
  const [changePreviewLoading, setChangePreviewLoading] = useState(false);
  const [pendingActionConfirm, setPendingActionConfirm] = useState(null);
  const [purchaseConfirmation, setPurchaseConfirmation] = useState(null);
  const [showPlanManagement, setShowPlanManagement] = useState(false);
  const [showTaxExemptionManagement, setShowTaxExemptionManagement] = useState(false);
  const [visibleInvoiceCount, setVisibleInvoiceCount] = useState(5);
  const [showTaxExemptionModal, setShowTaxExemptionModal] = useState(false);
  const [pendingTaxExemptionWithdrawal, setPendingTaxExemptionWithdrawal] = useState(null);
  const [taxExemptionForm, setTaxExemptionForm] = useState({
    billing_legal_name: '',
    exemption_id_value: '',
    exemption_reason: '',
    contact_email: '',
    notes: '',
    attested: false,
  });
  const processedCheckoutSessionRef = useRef(null);

  const loadSubscription = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const response = await api.get('/acct-mgmt/v1/account/subscription');
      setData(response.data);
      return response.data;
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to load subscription details.');
      return null;
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadSubscription();
  }, [loadSubscription]);

  useEffect(() => {
    if (!data?.subscription) return;
    const storedNotice = getStoredItem(SUBSCRIPTION_NOTICE_KEY);
    if (storedNotice) {
      try {
        const parsed = JSON.parse(storedNotice);
        const isMatchingOrg = !parsed?.org_id || !data?.org_id || String(parsed.org_id) === String(data.org_id);
        const isExpired = parsed?.expires_at && Number(parsed.expires_at) < Date.now();
        if (isExpired || !isMatchingOrg) {
          removeStoredItem(SUBSCRIPTION_NOTICE_KEY);
        } else {
          if (parsed?.notice) setNotice(parsed.notice);
          setNoticeAction(parsed?.noticeAction || null);
          const remainingReads = Number(parsed?.remaining_reads || 0);
          if (remainingReads > 1) {
            setStoredItem(SUBSCRIPTION_NOTICE_KEY, JSON.stringify({
              ...parsed,
              remaining_reads: remainingReads - 1,
            }));
          } else {
            removeStoredItem(SUBSCRIPTION_NOTICE_KEY);
          }
        }
      } catch (err) {
        removeStoredItem(SUBSCRIPTION_NOTICE_KEY);
      }
    }
    const stored = getStoredItem(PURCHASE_CONFIRMATION_KEY);
    if (!stored) return;
    try {
      const parsed = JSON.parse(stored);
      if (parsed?.org_id && data?.org_id && String(parsed.org_id) !== String(data.org_id)) {
        return;
      }
      if (parsed?.notice) setNotice(parsed.notice);
      setNoticeAction(null);
      if (parsed?.confirmation) setPurchaseConfirmation(parsed.confirmation);
      removeStoredItem(PURCHASE_CONFIRMATION_KEY);
    } catch (err) {
      removeStoredItem(PURCHASE_CONFIRMATION_KEY);
    }
  }, [data]);

  useEffect(() => {
    const taxExemption = data?.tax_exemption || {};
    setTaxExemptionForm({
      billing_legal_name: taxExemption.billing_legal_name || data?.billing_legal_name || data?.org_name || '',
      exemption_id_value: '',
      exemption_reason: '',
      contact_email: data?.current_user_email || taxExemption.current_request?.contact_email || '',
      notes: '',
      attested: false,
    });
  }, [data]);

  useEffect(() => {
    const search = new URLSearchParams(window.location.search);
    const checkoutStatus = search.get('stripe_checkout');
    const sessionId = search.get('session_id');
    if (!checkoutStatus) return;

    const clearParams = () => {
      const next = new URL(window.location.href);
      next.searchParams.delete('stripe_checkout');
      next.searchParams.delete('session_id');
      window.history.replaceState({}, '', `${next.pathname}${next.search}`);
    };

    if (checkoutStatus === 'cancel') {
      setNotice('Subscription checkout was canceled. No billing changes were made.');
      setNoticeAction(null);
      clearParams();
      return;
    }

    if (checkoutStatus === 'success' && sessionId) {
      if (processedCheckoutSessionRef.current === sessionId) {
        clearParams();
        return;
      }
      processedCheckoutSessionRef.current = sessionId;
      clearParams();
      let cancelled = false;
      setActionLoading('complete-checkout');
      api.post('/acct-mgmt/v1/account/subscription/complete', { session_id: sessionId })
        .then(async () => {
          if (cancelled) return;
          const nextData = await loadSubscription();
          if (cancelled) return;
          const confirmation = nextData?.subscription ? buildPurchaseConfirmation({
              subscription: nextData.subscription,
              type: 'activated',
            }) : null;
          const successNotice = 'Subscription activated successfully.';
          setStoredItem(PURCHASE_CONFIRMATION_KEY, JSON.stringify({
            org_id: nextData?.org_id || data?.org_id || null,
            notice: successNotice,
            confirmation,
          }));
          if (onUpdated) await onUpdated();
          if (confirmation) setPurchaseConfirmation(confirmation);
          setNotice(successNotice);
          setNoticeAction(null);
        })
        .catch((err) => {
          if (cancelled) return;
          setError(err.response?.data?.error || 'Failed to finalize subscription checkout.');
        })
        .finally(() => {
          if (cancelled) return;
          setActionLoading('');
        });
      return () => {
        cancelled = true;
      };
    }
    clearParams();
    return undefined;
  }, [loadSubscription]);

  const subscription = data?.subscription;
  const taxExemption = data?.tax_exemption;
  const availablePlans = useMemo(() => data?.available_plans || [], [data]);
  const currentOffering = useMemo(() => {
    if (!subscription?.plan_code) return null;
    return availablePlans.find((plan) => plan.plan_code === subscription.plan_code) || null;
  }, [availablePlans, subscription?.plan_code]);
  const billingHistory = useMemo(() => data?.billing_history || [], [data]);
  const visibleBillingHistory = useMemo(
    () => billingHistory.slice(0, visibleInvoiceCount),
    [billingHistory, visibleInvoiceCount]
  );
  const canSubmitTaxExemptionForm = Boolean(
    taxExemptionForm.billing_legal_name.trim()
    && taxExemption?.billing_jurisdiction
    && taxExemptionForm.exemption_id_value.trim()
    && taxExemptionForm.contact_email.trim()
    && taxExemptionForm.exemption_reason.trim()
    && taxExemptionForm.attested
  );
  const canStartCheckout = subscription && !subscription.is_internal
    && ['trialing', 'none', 'expired', 'canceled'].includes(subscription.status);
  const hasPaidSubscription = subscription && !subscription.is_internal
    && ['active', 'scheduled_cancel', 'scheduled_downgrade'].includes(subscription.raw_status || subscription.status);
  const canChangePaidSubscription = subscription && !subscription.is_internal
    && ['active', 'scheduled_downgrade'].includes(subscription.raw_status || subscription.status)
    && !subscription?.scheduled_change;
  const visibleGrandfatherEntitlementChanges = getCustomerVisibleEntitlementChanges(subscription?.grandfather_sunset?.entitlement_changes);
  const visibleScheduledEntitlementChanges = getCustomerVisibleEntitlementChanges(subscription?.scheduled_change?.entitlement_changes);

  useEffect(() => {
    setShowPlanManagement(Boolean(canStartCheckout));
  }, [canStartCheckout, canChangePaidSubscription]);

  useEffect(() => {
    setVisibleInvoiceCount(5);
  }, [billingHistory.length]);

  const handleCheckout = async (planCode, cadence) => {
    setError('');
    setNotice('');
    setStoredItem(CHECKOUT_IN_PROGRESS_KEY, JSON.stringify({
      plan_code: planCode,
      billing_cadence: cadence,
      started_at: Date.now(),
    }));
    history.push(`/account/subscription/checkout?plan_code=${encodeURIComponent(planCode)}&billing_cadence=${encodeURIComponent(cadence)}`);
  };

  const waitForBillingHistoryRefresh = useCallback(async ({ previousInvoiceCount, attempts = 6, delayMs = 1500 }) => {
    for (let index = 0; index < attempts; index += 1) {
      await new Promise((resolve) => { window.setTimeout(resolve, delayMs); });
      try {
        const response = await api.get('/acct-mgmt/v1/account/subscription');
        const nextData = response.data;
        const nextCount = Array.isArray(nextData?.billing_history) ? nextData.billing_history.length : 0;
        setData(nextData);
        if (nextCount > previousInvoiceCount) {
          return;
        }
      } catch (err) {
        return;
      }
    }
  }, []);

  const handleSubscriptionAction = async (action) => {
    setActionLoading(action);
    setError('');
    setNotice('');
    try {
      await api.post(`/acct-mgmt/v1/account/subscription/${action}`, {});
      setNotice(action === 'cancel'
        ? 'Subscription is set to stop renewing at the end of the current term.'
        : 'Subscription renewal has been restored.');
      await loadSubscription();
      if (onUpdated) await onUpdated();
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to update subscription.');
    } finally {
      setActionLoading('');
    }
  };

  const actionConfirmContent = (() => {
    if (!pendingActionConfirm || !subscription) return null;
    if (pendingActionConfirm === 'cancel') {
      return {
        title: 'Confirm Cancellation',
        body: 'This will stop the subscription from renewing at the end of the current term. Access will remain active through the current term.',
        confirmLabel: 'Confirm Cancellation',
      };
    }
    if (pendingActionConfirm === 'resume') {
      return {
        title: 'Undo Scheduled Cancellation',
        body: 'This will restore automatic renewal for the current subscription term.',
        confirmLabel: 'Undo Scheduled Cancellation',
      };
    }
    if (pendingActionConfirm === 'clear-change') {
      return {
        title: 'Remove Scheduled Change',
        body: 'This will remove the scheduled subscription change and keep the current subscription unchanged going forward.',
        confirmLabel: 'Remove Scheduled Change',
      };
    }
    return null;
  })();

  const handlePlanChange = async (planCode, cadence) => {
    setError('');
    setNotice('');
    try {
      setChangePreviewLoading(true);
      const response = await api.post('/acct-mgmt/v1/account/subscription/change-preview', {
        plan_code: planCode,
        billing_cadence: cadence,
      });
      setChangePreview({
        ...response.data,
        request: {
          plan_code: planCode,
          billing_cadence: cadence,
        },
      });
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to preview subscription change.');
    } finally {
      setChangePreviewLoading(false);
    }
  };

  const confirmPlanChange = async () => {
    if (!changePreview?.request) return;
    const { plan_code, billing_cadence } = changePreview.request;
    const actionKey = `change-${plan_code}-${billing_cadence}`;
    setActionLoading(actionKey);
    setError('');
    setNotice('');
    try {
      const previousInvoiceCount = billingHistory.length;
      const response = await api.post('/acct-mgmt/v1/account/subscription/change-plan', {
        plan_code,
        billing_cadence,
      });
      setChangePreview(null);
      const nextData = await loadSubscription();
      const successNotice = response.data?.mode === 'immediate'
        ? 'Subscription updated successfully.'
        : 'Subscription change scheduled for the end of the current term.';
      let confirmation = null;
      if (response.data?.mode === 'immediate' && nextData?.subscription) {
        confirmation = buildPurchaseConfirmation({
          subscription: nextData.subscription,
          type: 'updated',
        });
        setStoredItem(PURCHASE_CONFIRMATION_KEY, JSON.stringify({
          org_id: nextData?.org_id || data?.org_id || null,
          notice: successNotice,
          confirmation,
        }));
      }
      if (onUpdated) await onUpdated();
      if (confirmation) setPurchaseConfirmation(confirmation);
      setNotice(successNotice);
      if (response.data?.mode === 'immediate') {
        await waitForBillingHistoryRefresh({ previousInvoiceCount });
      }
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to change subscription.');
    } finally {
      setActionLoading('');
    }
  };

  const handleClearScheduledChange = async () => {
    setActionLoading('clear-change');
    setError('');
    setNotice('');
    try {
      await api.post('/acct-mgmt/v1/account/subscription/clear-change', {});
      setNotice('Scheduled subscription change removed.');
      await loadSubscription();
      if (onUpdated) await onUpdated();
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to remove the scheduled subscription change.');
    } finally {
      setActionLoading('');
    }
  };

  const handleTaxExemptionFieldChange = (event) => {
    const { name, value, type, checked } = event.target;
    setTaxExemptionForm((prev) => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value,
    }));
  };

  const handleSubmitTaxExemptionRequest = async () => {
    setActionLoading('tax-exemption-submit');
    setError('');
    setNotice('');
    setNoticeAction(null);
    try {
      const response = await api.post('/acct-mgmt/v1/account/tax-exemption-requests', taxExemptionForm);
      const jurisdiction = response.data?.jurisdiction_state || taxExemption?.billing_jurisdiction || 'the current billing state';
      const supportTicketUrl = response.data?.support_ticket?.url || null;
      const supportUrl = supportTicketUrl || response.data?.support_url || data?.support_url || 'https://support.finbaseco.com';
      const nextNotice = supportTicketUrl
        ? `Tax exemption request for ${jurisdiction} has been submitted for review, and a support ticket has been created where supporting documentation can be uploaded. Please upload supporting documentation to the linked support ticket.`
        : response.data?.support_ticket_warning
          ? `Tax exemption request for ${jurisdiction} has been submitted for review, but the linked support ticket could not be created automatically. Please open the Finbase Support Portal to submit supporting documentation for review.`
          : `Tax exemption request submitted for ${jurisdiction}. Open the Finbase Support Portal to submit supporting documentation for review.`;
      const nextNoticeAction = {
        label: supportTicketUrl ? 'View Support Ticket & Upload Documentation' : 'Open Support Portal',
        href: supportUrl,
        target: '_blank',
      };
      setNotice(nextNotice);
      setNoticeAction(nextNoticeAction);
      setStoredItem(SUBSCRIPTION_NOTICE_KEY, JSON.stringify({
        org_id: data?.org_id || null,
        notice: nextNotice,
        noticeAction: nextNoticeAction,
        expires_at: Date.now() + (10 * 60 * 1000),
        remaining_reads: 2,
      }));
      await loadSubscription();
      if (onUpdated) await onUpdated();
      return true;
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to submit tax exemption request.');
      return false;
    } finally {
      setActionLoading('');
    }
  };

  const handleWithdrawTaxExemptionRequest = async (request) => {
    if (!request?.id) return;
    setActionLoading(`tax-exemption-withdraw-${request.id}`);
    setError('');
    setNotice('');
    setNoticeAction(null);
    try {
      const response = await api.post(`/acct-mgmt/v1/account/tax-exemption-requests/${request.id}/withdraw`, {});
      setNotice(response.data?.message || 'Tax exemption request updated.');
      setPendingTaxExemptionWithdrawal(null);
      await loadSubscription();
      if (onUpdated) await onUpdated();
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to update tax exemption request.');
    } finally {
      setActionLoading('');
    }
  };

  return (
    <div>
      <div className="mb-3">
        <h2 className="h4 fw-bold mb-1">Subscription</h2>
        <p className="text-muted mb-0">Manage your subscription and billing settings.</p>
      </div>
      {error && <div className="alert alert-danger" role="alert">{error}</div>}
      {notice && (
        <div className="alert alert-success" role="alert">
          <div>{notice}</div>
          {noticeAction?.href && (
            <div className="mt-2">
              <a
                href={noticeAction.href}
                target={noticeAction.target || undefined}
                rel={noticeAction.target === '_blank' ? 'noreferrer' : undefined}
              >
                {noticeAction.label}
              </a>
            </div>
          )}
        </div>
      )}
      {purchaseConfirmation && (
        <>
          <div className="modal fade show d-block" tabIndex="-1" role="dialog" aria-modal="true">
            <div className="modal-dialog modal-dialog-centered" role="document">
              <div className="modal-content">
                <div className="modal-header">
                  <h5 className="modal-title">{purchaseConfirmation.title}</h5>
                  <button type="button" className="btn-close" aria-label="Close" onClick={() => setPurchaseConfirmation(null)} />
                </div>
                <div className="modal-body">
                  <p className="mb-3">{purchaseConfirmation.message}</p>
                  <div className="row g-3 mb-3">
                    <div className="col-md-6">
                      <div className="text-muted small">Plan</div>
                      <div className="fw-semibold">{purchaseConfirmation.planName}</div>
                    </div>
                    <div className="col-md-6">
                      <div className="text-muted small">Billing Cadence</div>
                      <div className="fw-semibold">{purchaseConfirmation.billingCadence}</div>
                    </div>
                  </div>
                  <div className="fw-semibold mb-2">Active API Permissions</div>
                  <div className="row g-3">
                    <div className="col-md-6">
                      <div className="text-muted small">Monthly API Limit</div>
                      <div className="fw-semibold">{formatApiLimit(purchaseConfirmation.entitlements?.api_rate_limit)}</div>
                    </div>
                    <div className="col-md-6">
                      <div className="text-muted small">Data Sources</div>
                      <div className="fw-semibold">{purchaseConfirmation.entitlements?.api_src_limit || 'All Data Sources'}</div>
                    </div>
                    <div className="col-md-6">
                      <div className="text-muted small">Derived Data</div>
                      <div className="fw-semibold">{purchaseConfirmation.entitlements?.api_src_derived_flag ? 'Enabled' : 'Not Included'}</div>
                    </div>
                    <div className="col-md-6">
                      <div className="text-muted small">History Access</div>
                      <div className="fw-semibold">{formatHistory(purchaseConfirmation.entitlements?.api_hist_limit)}</div>
                    </div>
                  </div>
                  <div className="fw-semibold mt-4 mb-2">Active Account Features</div>
                  <div className="row g-3">
                    <div className="col-md-6">
                      <div className="text-muted small">SSO / Custom IdP</div>
                      <div className="fw-semibold">
                        <IncludedValue included={Boolean(purchaseConfirmation.accountFeatures?.sso_custom_idp)} />
                      </div>
                    </div>
                  </div>
                </div>
                <div className="modal-footer">
                  <button type="button" className="btn btn-primary" onClick={() => setPurchaseConfirmation(null)}>Close</button>
                </div>
              </div>
            </div>
          </div>
          <div className="modal-backdrop fade show" />
        </>
      )}
      <div className="card mb-4">
        <div className="card-body">
          <div className="d-flex flex-wrap align-items-start justify-content-between gap-3 pb-3 mb-3 border-bottom">
            <div>
              <h5 className="mb-1">Current Subscription Details</h5>
              <div className="text-muted small">Current subscription status, entitlements, and billing profile.</div>
            </div>
          </div>
          {loading && <div className="text-muted">Loading subscription details...</div>}
          {!loading && subscription?.is_internal && (
            <div>
              <div className="fw-semibold mb-1">Internal Access</div>
              <div className="text-muted">This organization is marked internal. Subscription billing does not apply and API access is unrestricted.</div>
            </div>
          )}
          {!loading && !subscription?.is_internal && (
            <>
              <div className="row g-3">
                <div className="col-md-3">
                  <div className="text-muted small">Current Subscription</div>
                  <div className="fw-semibold">{subscription?.plan_name || 'No Active Subscription'}</div>
                </div>
                <div className="col-md-3">
                  <div className="text-muted small">Status</div>
                  <div className="fw-semibold text-capitalize">{subscription?.status?.replace(/_/g, ' ') || 'none'}</div>
                </div>
                <div className="col-md-3">
                  <div className="text-muted small">Billing Cadence</div>
                  <div className="fw-semibold">{formatCadence(subscription?.billing_cadence)}</div>
                    </div>
                <div className="col-md-3">
                  <div className="text-muted small">{getActivePriceLabel(subscription)}</div>
                  <div className="fw-semibold">{getActivePriceValue(subscription)}</div>
                  <div className="text-muted small mt-1">Plus tax, where applicable.</div>
                </div>
                {shouldShowTrialEnd(subscription) && (
                  <div className="col-md-3">
                    <div className="text-muted small">Trial Ends</div>
                    <div className="fw-semibold">{formatAccessThroughDate(subscription?.trial_end)}</div>
                  </div>
                )}
                {shouldShowNextRenewal(subscription) && (
                  <div className="col-md-3">
                    <div className="text-muted small">Next Renewal / Charge Date</div>
                    <div className="fw-semibold">{formatDate(subscription?.billing_period_end)}</div>
                    {hasPaidSubscription && (subscription.raw_status || subscription.status) !== 'scheduled_cancel' && !subscription?.scheduled_change && (
                      <div className="mt-2">
                        <button
                          type="button"
                          className="btn btn-outline-danger btn-sm"
                          onClick={() => setPendingActionConfirm('cancel')}
                          disabled={Boolean(actionLoading)}
                        >
                          {actionLoading === 'cancel' ? 'Scheduling...' : 'Cancel at End of Term'}
                        </button>
                      </div>
                    )}
                  </div>
                )}
              </div>

              {subscription?.grandfather_sunset && (
                <div className="small px-3 py-2 mt-3 rounded border-start border-3 border-info bg-light">
                  <div className="fw-semibold mb-1">Legacy Pricing Update</div>
                  <div>
                    Your current plan pricing will update on {formatDate(subscription.grandfather_sunset.effective_change_date || subscription.grandfather_sunset.grandfather_end_date)}.
                  </div>
                  {subscription.grandfather_sunset.price_changes ? (
                    <div>
                      {getGrandfatherSunsetPriceLabel(subscription)} changes from {formatMoney(subscription.grandfather_sunset.current_price_cents)} to {formatMoney(subscription.grandfather_sunset.target_price_cents)}.
                    </div>
                  ) : (
                    <div>Your recurring price will not change.</div>
                  )}
                  <div className="text-muted">Plus tax, where applicable.</div>
                </div>
              )}

              {subscription?.scheduled_cancellation && (
                <div className="small px-3 py-2 mt-3 rounded border-start border-3 border-info bg-light">
                  <div className="fw-semibold mb-1">Scheduled cancellation</div>
                  <div>
                    Your subscription will stop renewing at the end of the current term.
                  </div>
                  <div>
                    Access through {formatAccessThroughDate(subscription.scheduled_cancellation.access_through)}.
                  </div>
                  <div className="text-muted">
                    Your current plan remains active until then.
                  </div>
                  <div className="mt-2">
                    <button
                      type="button"
                      className="btn btn-outline-primary btn-sm"
                      onClick={() => setPendingActionConfirm('resume')}
                      disabled={Boolean(actionLoading)}
                    >
                      {actionLoading === 'resume' ? 'Restoring...' : 'Undo Scheduled Cancellation'}
                    </button>
                  </div>
                </div>
              )}

              {subscription?.scheduled_change && (
                <div className="small px-3 py-2 mt-3 rounded border-start border-3 border-info bg-light">
                  <div className="fw-semibold mb-1">Scheduled Subscription Update</div>
                  <div>
                    Your subscription will change to {subscription.scheduled_change.plan_name || 'the selected plan'} / {formatCadence(subscription.scheduled_change.billing_cadence)} on {formatDate(subscription.scheduled_change.effective_on)}.
                  </div>
                  <div>
                    Estimated {String(subscription.scheduled_change.billing_cadence || '').toLowerCase() || 'recurring'} price: {formatMoney(subscription.scheduled_change.recurring_price_cents)}.
                  </div>
                  <div className="text-muted">
                    The rate in effect on that date will apply.
                  </div>
                  <div className="text-muted">
                    Plus tax, where applicable.
                  </div>
                  <div className="mt-2">
                    <button
                      type="button"
                      className="btn btn-outline-secondary btn-sm"
                      onClick={() => setPendingActionConfirm('clear-change')}
                      disabled={Boolean(actionLoading)}
                    >
                      {actionLoading === 'clear-change' ? 'Removing...' : 'Remove Scheduled Change'}
                    </button>
                  </div>
                  <div className="text-muted mt-2">To cancel at the end of the current term, remove the scheduled change first.</div>
                </div>
              )}

              <div className="mt-4 pt-3 border-top">
                <div className="fw-semibold mb-2">API Permissions</div>
                <div className="row g-3">
                  <div className="col-md-3">
                    <div className="text-muted small">Monthly API Limit</div>
                    <div className="fw-semibold">{formatApiLimit(subscription?.entitlements?.api_rate_limit)}</div>
                  </div>
                  <div className="col-md-3">
                    <div className="text-muted small">Data Sources</div>
                    <div className="fw-semibold">{subscription?.entitlements?.api_src_limit || 'All Data Sources'}</div>
                  </div>
                  <div className="col-md-3">
                    <div className="text-muted small">Derived Data</div>
                    <div className="fw-semibold">{subscription?.entitlements?.api_src_derived_flag ? 'Enabled' : 'Not Included'}</div>
                  </div>
                  <div className="col-md-3">
                    <div className="text-muted small">History Access</div>
                    <div className="fw-semibold">{formatHistory(subscription?.entitlements?.api_hist_limit)}</div>
                  </div>
                </div>
                {subscription?.grandfather_sunset && (
                  <div className="d-flex flex-wrap align-items-center gap-3 small px-3 py-2 mt-3 rounded border-start border-3 border-info bg-light">
                    <span className="fw-semibold">Legacy Plan Entitlements Update</span>
                    <span>
                      Changes take effect on {formatDate(subscription.grandfather_sunset.effective_change_date || subscription.grandfather_sunset.grandfather_end_date)}.
                    </span>
                    {visibleGrandfatherEntitlementChanges.length > 0 ? (
                      <EntitlementChangesTable changes={visibleGrandfatherEntitlementChanges} />
                    ) : (
                      <span>This upcoming subscription change will not result in any entitlement changes.</span>
                    )}
                  </div>
                )}
                {subscription?.scheduled_change && (
                  <div className="d-flex flex-wrap align-items-center gap-3 small px-3 py-2 mt-3 rounded border-start border-3 border-info bg-light">
                    <span className="fw-semibold">Scheduled Plan Entitlements Update</span>
                    <span>
                      Estimated changes take effect on {formatDate(subscription.scheduled_change.effective_on)}.
                    </span>
                    {visibleScheduledEntitlementChanges.length > 0 ? (
                      <EntitlementChangesTable changes={visibleScheduledEntitlementChanges} />
                    ) : (
                      <span>Based on current plan terms, this scheduled change will not change your entitlements.</span>
                    )}
                    <span className="text-muted">Final entitlements will reflect the plan terms in effect on that date.</span>
                  </div>
                )}
              </div>

              <div className="mt-4 pt-3 border-top">
                <div className="fw-semibold mb-2">Account Features</div>
                <div className="row g-3">
                  <div className="col-md-3">
                    <div className="text-muted small">SSO / Custom IdP</div>
                    <div className="fw-semibold">{formatAccountFeatureValue(getSsoAccountFeature(subscription))}</div>
                  </div>
                </div>
                <AccountFeatureChanges
                  changes={subscription?.grandfather_sunset?.account_feature_changes}
                  effectiveLabel={subscription?.grandfather_sunset
                    ? `Changes take effect on ${formatDate(subscription.grandfather_sunset.effective_change_date || subscription.grandfather_sunset.grandfather_end_date)}.`
                    : ''}
                />
                <AccountFeatureChanges
                  changes={subscription?.scheduled_change?.account_feature_changes}
                  effectiveLabel={subscription?.scheduled_change
                    ? `Estimated changes take effect on ${formatDate(subscription.scheduled_change.effective_on)}.`
                    : ''}
                />
              </div>

              <div className="mt-4 pt-3 border-top">
                <div className="fw-semibold mb-2">Billing and Tax Status</div>
                <div className="row g-3">
                  <div className="col-md-4">
                    <div className="text-muted small">Billing Address</div>
                    <div className="fw-semibold">{formatBillingAddress(data)}</div>
                  </div>
                  <div className="col-md-4">
                    <div className="text-muted small">Billing Legal Name</div>
                    <div className="fw-semibold">{data?.billing_legal_name || data?.org_name || '—'}</div>
                  </div>
                  <div className="col-md-4">
                    <div className="text-muted small">Tax Exempt Status</div>
                    <div className="fw-semibold text-capitalize">{String(data?.tax_exempt_status || 'none').replace(/_/g, ' ')}</div>
                  </div>
                </div>
                {taxExemption?.mismatch_message && (
                  <div className="alert alert-warning mt-3 mb-0" role="alert">
                    {taxExemption.mismatch_message}
                  </div>
                )}
              </div>
            </>
          )}
        </div>
      </div>

              {!loading && !subscription?.is_internal && (canStartCheckout || canChangePaidSubscription) && (
        <div className="card mb-4">
          <div className="card-body">
            <div className={`d-flex flex-wrap justify-content-between align-items-start gap-3${showPlanManagement ? ' pb-3 mb-3 border-bottom' : ''}`}>
              <div>
                <h5 className="mb-1">{getManageCardTitle(subscription, canStartCheckout, canChangePaidSubscription)}</h5>
                <div className="text-muted small">
                  Upgrades apply immediately. Downgrades and annual-to-monthly changes apply at the end of the current term.
                </div>
              </div>
              <button
                type="button"
                className="btn btn-outline-secondary btn-sm"
                onClick={() => setShowPlanManagement((prev) => !prev)}
              >
                {showPlanManagement ? 'Hide Plan Options' : 'Manage Plan'}
              </button>
            </div>

            {showPlanManagement && (
              <div>
                {canStartCheckout && (
                  <div>
                    <div className="fw-semibold mb-2">Choose a Subscription</div>
                    <div className="row g-3">
                      {availablePlans.map((plan) => (
                        <div className="col-lg-4" key={`${plan.plan_code}-${plan.version_code}`}>
                          <div className="border rounded p-3 h-100">
                            <div className="fw-semibold mb-1">{plan.plan_name}</div>
                            <div className="small mb-1">
                              <strong>Monthly:</strong> {formatMoney(plan.price_monthly_cents)}
                            </div>
                            <div className="text-muted small mb-1">Plus tax, where applicable.</div>
                            <div className="small mb-1">
                              <strong>Annual:</strong> {formatMoney(plan.price_annual_cents)}
                            </div>
                            <div className="text-muted small mb-1">Plus tax, where applicable.</div>
                            <div className="text-muted small mb-3">
                              Save {plan.annual_discount_pct || 0}% with annual billing — {formatMoney(getAnnualSavingsCents(plan))} per year.
                            </div>
                            <div className="fw-semibold small mt-3 mb-1">API Permissions</div>
                            <PlanFeatureRow label="API Limit">{formatApiLimit(plan.api_rate_limit)} requests per month</PlanFeatureRow>
                            <PlanFeatureRow label="Data Sources">{plan.api_src_limit || 'All Data Sources'}</PlanFeatureRow>
                            <PlanFeatureRow label="Derived Data"><IncludedValue included={Boolean(plan.api_src_derived_flag)} /></PlanFeatureRow>
                            <PlanFeatureRow label="History" className="mb-3">{formatHistory(plan.api_hist_limit)}</PlanFeatureRow>
                            <div className="fw-semibold small mt-3 mb-1">Account Features</div>
                            <PlanFeatureRow label="SSO / Custom IdP" className="mb-3"><IncludedValue included={getSsoAccountFeature(plan)} /></PlanFeatureRow>
                            <div className="d-flex flex-wrap gap-2">
                              <button
                                type="button"
                                className="btn btn-outline-primary btn-sm"
                                onClick={() => handleCheckout(plan.plan_code, 'monthly')}
                                disabled={Boolean(actionLoading)}
                              >
                                Choose Monthly
                              </button>
                              <button
                                type="button"
                                className="btn btn-primary btn-sm"
                                onClick={() => handleCheckout(plan.plan_code, 'annual')}
                                disabled={Boolean(actionLoading)}
                              >
                                Choose Annual
                              </button>
                            </div>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                {canChangePaidSubscription && (
                  <div>
                    <div className="row g-3">
                      {availablePlans.map((plan) => (
                        <div className="col-lg-4" key={`change-${plan.plan_code}-${plan.version_code}`}>
                          <div
                            className="border rounded p-3 h-100 d-flex flex-column"
                            style={subscription?.plan_code === plan.plan_code
                              ? {
                                  borderColor: '#0a58ca',
                                  borderWidth: '3px',
                                  boxShadow: '0 0 0 3px rgba(13,110,253,0.14)',
                                }
                              : undefined}
                          >
                            <div className="d-flex align-items-center justify-content-between gap-2 mb-1">
                              <div className="fw-semibold">{plan.plan_name}</div>
                              {subscription?.plan_code === plan.plan_code && (
                                <span className="badge bg-primary">Current Plan</span>
                              )}
                            </div>
                            <div className="small mb-1">
                              <strong>Monthly:</strong> {formatMoney(plan.price_monthly_cents)}
                            </div>
                            <div className="text-muted small mb-1">Plus tax, where applicable.</div>
                            <div className="small mb-1">
                              <strong>Annual:</strong> {formatMoney(plan.price_annual_cents)}
                            </div>
                            <div className="text-muted small mb-1">Plus tax, where applicable.</div>
                            <div className="text-muted small mb-3">
                              Save {plan.annual_discount_pct || 0}% with annual billing — {formatMoney(getAnnualSavingsCents(plan))} per year.
                            </div>
                            <div className="fw-semibold small mt-3 mb-1">API Permissions</div>
                            <PlanFeatureRow label="API Limit">{formatApiLimit(plan.api_rate_limit)} requests per month</PlanFeatureRow>
                            <PlanFeatureRow label="Data Sources">{plan.api_src_limit || 'All Data Sources'}</PlanFeatureRow>
                            <PlanFeatureRow label="Derived Data"><IncludedValue included={Boolean(plan.api_src_derived_flag)} /></PlanFeatureRow>
                            <PlanFeatureRow label="History" className="mb-3">{formatHistory(plan.api_hist_limit)}</PlanFeatureRow>
                            <div className="fw-semibold small mt-3 mb-1">Account Features</div>
                            <PlanFeatureRow label="SSO / Custom IdP" className="mb-3"><IncludedValue included={getSsoAccountFeature(plan)} /></PlanFeatureRow>
                            {subscription?.plan_code === plan.plan_code && subscription?.grandfather_sunset && (
                              <div className="small px-3 py-2 mb-3 rounded border-start border-3 border-info bg-light">
                                Your organization is on legacy {subscription.plan_name || plan.plan_name} pricing at {getActivePriceValue(subscription)} until {formatDate(subscription.grandfather_sunset.effective_change_date || subscription.grandfather_sunset.grandfather_end_date)}. Current access is shown above in Current Subscription Details; this card shows the latest {plan.plan_name} offering.
                              </div>
                            )}
                            <div className="d-grid gap-2 mt-auto">
                              {(['monthly', 'annual']).map((cadence) => {
                                const mode = getSubscriptionChangeMode(subscription, plan.plan_code, cadence);
                                const actionKey = `change-${plan.plan_code}-${cadence}`;
                                const isCurrent = mode === 'none';
                                const isPrimary = cadence === 'annual';
                                return (
                                  <button
                                    key={actionKey}
                                    type="button"
                                    className={`btn ${isCurrent ? 'btn-secondary' : (isPrimary ? 'btn-primary' : 'btn-outline-primary')} btn-sm w-100`}
                                    onClick={() => handlePlanChange(plan.plan_code, cadence)}
                                    disabled={Boolean(actionLoading) || changePreviewLoading || isCurrent}
                                  >
                                    {actionLoading === actionKey
                                      ? (mode === 'immediate' ? 'Updating...' : 'Scheduling...')
                                      : changePreviewLoading && changePreview?.request?.plan_code === plan.plan_code && changePreview?.request?.billing_cadence === cadence
                                        ? 'Preparing...'
                                      : getDetailedChangeButtonLabel({ subscription, planCode: plan.plan_code, cadence })}
                                  </button>
                                );
                              })}
                            </div>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            )}
          </div>
        </div>
      )}

      {!loading && !subscription?.is_internal && (
        <div className="card mb-4">
          <div className="card-body">
            <div className={`d-flex flex-wrap justify-content-between align-items-start gap-3${showTaxExemptionManagement ? ' pb-3 mb-3 border-bottom' : ''}`}>
              <div>
                <h5 className="mb-1">Tax Exemption Requests</h5>
                <div className="text-muted small">
                  Submit and track tax exemption requests for the current billing state.
                </div>
              </div>
              <button
                type="button"
                className="btn btn-outline-secondary btn-sm"
                onClick={() => setShowTaxExemptionManagement((prev) => !prev)}
              >
                {showTaxExemptionManagement ? 'Hide Tax Exemptions' : 'Manage Tax Exemptions'}
              </button>
            </div>

            {showTaxExemptionManagement && (
              <div>
                <div className="d-flex flex-wrap justify-content-between align-items-start gap-3 mb-3">
                  <div className="text-muted small">
                    Documentation will be required after submission to support the review process.
                  </div>
                  {taxExemption?.can_submit_request && (
                    <button
                      type="button"
                      className="btn btn-primary btn-sm"
                      onClick={() => setShowTaxExemptionModal(true)}
                      disabled={Boolean(actionLoading)}
                    >
                      Submit New Request
                    </button>
                  )}
                </div>
                <div className="row g-3 mb-3">
                  <div className="col-md-4">
                    <div className="text-muted small">Current Billing State</div>
                    <div className="fw-semibold">{taxExemption?.billing_jurisdiction || '—'}</div>
                  </div>
                  <div className="col-md-4">
                    <div className="text-muted small">Current Tax Exempt Status</div>
                    <div className="fw-semibold text-capitalize">{String(taxExemption?.status || 'none').replace(/_/g, ' ')}</div>
                  </div>
                  <div className="col-md-4">
                    <div className="text-muted small">Approved Jurisdictions</div>
                    <div className="fw-semibold">{taxExemption?.approved_jurisdictions?.length ? taxExemption.approved_jurisdictions.join(', ') : '—'}</div>
                  </div>
                </div>
                {!taxExemption?.billing_address_complete && (
                  <div className="alert alert-warning" role="alert">
                    Complete the billing address in Organization before requesting tax exemption.
                  </div>
                )}
                {taxExemption?.mismatch_message && (
                  <div className="alert alert-warning mb-3" role="alert">
                    {taxExemption.mismatch_message}
                  </div>
                )}
                {Array.isArray(taxExemption?.recent_requests) && taxExemption.recent_requests.length > 0 ? (
                  <div className="table-responsive">
                    <table className="table table-sm align-middle mb-0">
                      <thead>
                        <tr>
                          <th>Submitted</th>
                          <th>Jurisdiction</th>
                          <th>Exemption ID</th>
                          <th>Contact Email</th>
                          <th>Reason</th>
                          <th>Support Ticket</th>
                          <th>Decision Notes</th>
                          <th>Status</th>
                          <th>Actions</th>
                        </tr>
                      </thead>
                      <tbody>
                        {taxExemption.recent_requests.map((request) => {
                          const canWithdraw = ['pending_review', 'approved'].includes(String(request.status || ''));
                          return (
                            <tr key={request.id}>
                              <td>{formatDate(request.created_at)}</td>
                              <td>{request.jurisdiction_state}</td>
                              <td>{request.exemption_id_value || '—'}</td>
                              <td>{request.contact_email || '—'}</td>
                              <td>{request.exemption_reason || '—'}</td>
                              <td>
                                {request.support_ticket_url ? (
                                  <a href={request.support_ticket_url} target="_blank" rel="noreferrer">View Ticket</a>
                                ) : (
                                  <span className="text-muted small">—</span>
                                )}
                              </td>
                              <td>{request.decision_notes || '—'}</td>
                              <td className="text-capitalize">{String(request.status || '—').replace(/_/g, ' ')}</td>
                              <td>
                                {canWithdraw ? (
                                  <button
                                    type="button"
                                    className="btn btn-outline-danger btn-sm"
                                    onClick={() => setPendingTaxExemptionWithdrawal(request)}
                                    disabled={Boolean(actionLoading)}
                                  >
                                    {request.status === 'approved' ? 'Remove Exemption' : 'Undo Request'}
                                  </button>
                                ) : (
                                  <span className="text-muted small">—</span>
                                )}
                              </td>
                            </tr>
                          );
                        })}
                      </tbody>
                    </table>
                  </div>
                ) : (
                  <div className="border rounded px-3 py-2 bg-light">
                    <div className="text-muted small">No tax exemption requests have been submitted yet.</div>
                  </div>
                )}
              </div>
            )}
          </div>
        </div>
      )}

      {!loading && !subscription?.is_internal && (
        <div className="card">
          <div className="card-body">
            <h5 className="card-title mb-1">Billing History</h5>
            <div className="text-muted small mb-3">Recent invoices and payment outcomes.</div>
            {billingHistory.length === 0 ? (
              <div className="text-muted small">No invoices have been recorded yet.</div>
            ) : (
              <>
                <div className="table-responsive">
                  <table className="table table-sm align-middle mb-0">
                    <thead>
                      <tr>
                        <th>Invoice Date</th>
                        <th>Subtotal</th>
                        <th>Tax</th>
                        <th>Total</th>
                        <th>Status</th>
                        <th>Paid</th>
                        <th>Actions</th>
                      </tr>
                    </thead>
                    <tbody>
                      {visibleBillingHistory.map((invoice) => (
                        <tr key={invoice.stripe_invoice_id}>
                          <td>{formatDate(invoice.invoice_created_at)}</td>
                          <td>{formatMoney(invoice.subtotal_cents ?? invoice.amount_due_cents)} {formatCurrencyCode(invoice.currency)}</td>
                          <td>{formatMoney(invoice.tax_cents ?? 0)} {formatCurrencyCode(invoice.currency)}</td>
                          <td>{formatMoney(invoice.total_cents ?? invoice.amount_due_cents)} {formatCurrencyCode(invoice.currency)}</td>
                          <td className="text-capitalize">{String(invoice.status || '—').replace(/_/g, ' ')}</td>
                          <td>{formatMoney(invoice.amount_paid_cents)} {formatCurrencyCode(invoice.currency)}</td>
                          <td>
                            <div className="d-flex flex-wrap gap-2">
                              {invoice.hosted_invoice_url && (
                                <a href={invoice.hosted_invoice_url} target="_blank" rel="noreferrer" className="btn btn-outline-primary btn-sm">
                                  View
                                </a>
                              )}
                              {invoice.invoice_pdf && (
                                <a href={invoice.invoice_pdf} target="_blank" rel="noreferrer" className="btn btn-outline-secondary btn-sm">
                                  PDF
                                </a>
                              )}
                            </div>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
                {visibleInvoiceCount < billingHistory.length && (
                  <div className="mt-3">
                    <button
                      type="button"
                      className="btn btn-outline-secondary btn-sm"
                      onClick={() => setVisibleInvoiceCount((prev) => prev + 5)}
                    >
                      Show More
                    </button>
                  </div>
                )}
              </>
            )}
          </div>
        </div>
      )}
      {changePreview && (
        <>
          <div className="modal fade show d-block" tabIndex="-1" role="dialog" aria-modal="true">
            <div className="modal-dialog modal-dialog-centered" role="document">
              <div className="modal-content">
                <div className="modal-header">
                  <h5 className="modal-title">Confirm Subscription Change</h5>
                  <button type="button" className="btn-close" aria-label="Close" onClick={() => setChangePreview(null)} disabled={Boolean(actionLoading)}></button>
                </div>
                <div className="modal-body">
                  <div className="mb-3">
                    <div className="text-muted small">Current Subscription</div>
                    <div className="fw-semibold">
                      {changePreview.current?.plan_name} / {formatCadence(changePreview.current?.billing_cadence)}
                    </div>
                  </div>
                  <div className="mb-3">
                    <div className="text-muted small">Requested Change</div>
                    <div className="fw-semibold">
                      {changePreview.target?.plan_name} / {formatCadence(changePreview.target?.billing_cadence)}
                    </div>
                  </div>
                  <div className="mb-3">
                    <div className="text-muted small">Change Timing</div>
                    <div className="fw-semibold">
                      {changePreview.mode === 'immediate'
                        ? 'Applies immediately'
                        : `Applies at end of current term on ${formatDate(changePreview.effective_on)}`}
                    </div>
                  </div>
                  {changePreview.mode === 'immediate' ? (
                    <>
                      <div className="mb-2">
                        <div className="text-muted small">Subtotal</div>
                        <div className="fw-semibold">
                          {changePreview.preview_subtotal_cents === null || changePreview.preview_subtotal_cents === undefined
                            ? 'Calculated at confirmation'
                            : `${formatMoney(changePreview.preview_subtotal_cents)} ${formatCurrencyCode(changePreview.preview_currency)}`}
                        </div>
                      </div>
                      {Array.isArray(changePreview.preview_proration_lines) && changePreview.preview_proration_lines.length > 0 && (
                        <div className="mb-3">
                          <div className="text-muted small mb-2">Proration Details</div>
                          <div className="border rounded p-2 bg-light">
                            {changePreview.preview_proration_lines.map((line, index) => (
                              <div key={`${line.description}-${index}`} className={index < changePreview.preview_proration_lines.length - 1 ? 'pb-2 mb-2 border-bottom' : ''}>
                                <div className="d-flex justify-content-between gap-3">
                                  <div className="small">
                                    <div className="fw-semibold">{line.description}</div>
                                    {formatDateRange(line.period_start, line.period_end) && (
                                      <div className="text-muted">{formatDateRange(line.period_start, line.period_end)}</div>
                                    )}
                                  </div>
                                  <div className="small fw-semibold text-nowrap">
                                    {line.amount_cents < 0 ? '-' : ''}{formatMoney(Math.abs(line.amount_cents))} {formatCurrencyCode(line.currency || changePreview.preview_currency)}
                                  </div>
                                </div>
                              </div>
                            ))}
                          </div>
                        </div>
                      )}
                      <div className="mb-2">
                        <div className="text-muted small">
                          Tax{changePreview.preview_tax_cents > 0 && changePreview.preview_tax_label ? ` (${changePreview.preview_tax_label})` : ''}
                        </div>
                        <div className="fw-semibold">
                          {changePreview.preview_tax_cents === null || changePreview.preview_tax_cents === undefined
                            ? 'Calculated at confirmation'
                            : `${formatMoney(changePreview.preview_tax_cents)} ${formatCurrencyCode(changePreview.preview_currency)}`}
                        </div>
                      </div>
                      {changePreview.tax_exempt_message && (
                        <div className="text-muted small mb-2">{changePreview.tax_exempt_message}</div>
                      )}
                      <div className="mb-2">
                        <div className="text-muted small">Total Due Now</div>
                        <div className="fw-semibold">
                          {changePreview.preview_total_cents === null || changePreview.preview_total_cents === undefined
                            ? (changePreview.amount_due_now_cents === null || changePreview.amount_due_now_cents === undefined
                              ? 'Calculated at confirmation'
                              : `${formatMoney(changePreview.amount_due_now_cents)} ${formatCurrencyCode(changePreview.preview_currency)}`)
                            : `${formatMoney(changePreview.preview_total_cents)} ${formatCurrencyCode(changePreview.preview_currency)}`}
                        </div>
                      </div>
                      <div>
                        <div className="text-muted small">Next Recurring Charge</div>
                        <div className="fw-semibold">
                          {formatMoney(changePreview.next_recurring_amount_cents)} {formatCurrencyCode(changePreview.preview_currency)}
                        </div>
                        <div className="text-muted small mt-1">Plus tax, where applicable.</div>
                      </div>
                    </>
                  ) : (
                    <div>
                      <div className="text-muted small">Current Estimated Recurring Charge</div>
                      <div className="fw-semibold">
                        {formatMoney(changePreview.next_recurring_amount_cents)}
                      </div>
                      <div className="text-muted small mt-1">Plus tax, where applicable.</div>
                      <div className="text-muted small mt-1">
                        This amount reflects the current rate for this plan and billing cadence. If pricing changes before the scheduled conversion date, the rate in effect at that time will apply.
                      </div>
                    </div>
                  )}
                </div>
                <div className="modal-footer">
                  <button type="button" className="btn btn-outline-secondary" onClick={() => setChangePreview(null)} disabled={Boolean(actionLoading)}>
                    Cancel
                  </button>
                  <button type="button" className="btn btn-primary" onClick={confirmPlanChange} disabled={Boolean(actionLoading)}>
                    {actionLoading ? 'Applying...' : 'Confirm Change'}
                  </button>
                </div>
              </div>
            </div>
          </div>
          <div className="modal-backdrop fade show"></div>
        </>
      )}
      {pendingActionConfirm && actionConfirmContent && (
        <>
          <div className="modal fade show d-block" tabIndex="-1" role="dialog" aria-modal="true">
            <div className="modal-dialog modal-dialog-centered" role="document">
              <div className="modal-content">
                <div className="modal-header">
                  <h5 className="modal-title">{actionConfirmContent.title}</h5>
                  <button
                    type="button"
                    className="btn-close"
                    aria-label="Close"
                    onClick={() => setPendingActionConfirm(null)}
                    disabled={Boolean(actionLoading)}
                  ></button>
                </div>
                <div className="modal-body">
                  <div className="text-muted">{actionConfirmContent.body}</div>
                </div>
                <div className="modal-footer">
                  <button
                    type="button"
                    className="btn btn-outline-secondary"
                    onClick={() => setPendingActionConfirm(null)}
                    disabled={Boolean(actionLoading)}
                  >
                    Cancel
                  </button>
                  <button
                    type="button"
                    className="btn btn-primary"
                    onClick={async () => {
                      const action = pendingActionConfirm;
                      setPendingActionConfirm(null);
                      if (action === 'clear-change') {
                        await handleClearScheduledChange();
                      } else {
                        await handleSubscriptionAction(action);
                      }
                    }}
                    disabled={Boolean(actionLoading)}
                  >
                    {actionLoading ? 'Applying...' : actionConfirmContent.confirmLabel}
                  </button>
                </div>
              </div>
            </div>
          </div>
          <div className="modal-backdrop fade show"></div>
        </>
      )}
      {showTaxExemptionModal && (
        <>
          <div className="modal fade show d-block" tabIndex="-1" role="dialog" aria-modal="true">
            <div className="modal-dialog modal-dialog-centered modal-lg" role="document">
              <div className="modal-content">
                <div className="modal-header">
                  <h5 className="modal-title">Submit Tax Exemption Request</h5>
                  <button type="button" className="btn-close" aria-label="Close" onClick={() => setShowTaxExemptionModal(false)} disabled={Boolean(actionLoading)} />
                </div>
                <div className="modal-body">
                  <div className="mb-3 text-muted small">
                    Finbase will create a linked support ticket for supporting documentation after this request is submitted. Finbase will review the request and notify the listed contact once a decision is made. <strong>Approved tax exemption requests apply only to future purchases after approval and do not apply retroactively to prior purchases.</strong>
                  </div>
                  <div className="row g-3">
                    <div className="col-12">
                      <label className="form-label">Contact Email <span className="text-danger">*</span></label>
                      <input type="email" className="form-control" name="contact_email" value={taxExemptionForm.contact_email} onChange={handleTaxExemptionFieldChange} required />
                    </div>
                    <div className="col-12">
                      <label className="form-label">Billing Legal Name</label>
                      <input type="text" className="form-control" name="billing_legal_name" value={taxExemptionForm.billing_legal_name} readOnly disabled />
                    </div>
                    <div className="col-12">
                      <label className="form-label">Jurisdiction</label>
                      <input type="text" className="form-control" value={taxExemption?.billing_jurisdiction || ''} readOnly disabled />
                    </div>
                    <div className="col-12">
                      <label className="form-label">Tax Exemption ID / Certificate Number <span className="text-danger">*</span></label>
                      <input type="text" className="form-control" name="exemption_id_value" value={taxExemptionForm.exemption_id_value} onChange={handleTaxExemptionFieldChange} required />
                    </div>
                    <div className="col-12">
                      <label className="form-label">Exemption Reason <span className="text-danger">*</span></label>
                      <input type="text" className="form-control" name="exemption_reason" value={taxExemptionForm.exemption_reason} onChange={handleTaxExemptionFieldChange} required />
                    </div>
                    <div className="col-12">
                      <label className="form-label">Notes</label>
                      <textarea className="form-control" rows="3" name="notes" value={taxExemptionForm.notes} onChange={handleTaxExemptionFieldChange} />
                    </div>
                    <div className="col-12">
                      <div className="form-check d-flex align-items-start gap-2 mb-0">
                        <input className="form-check-input" type="checkbox" id="taxExemptionAttested" name="attested" checked={taxExemptionForm.attested} onChange={handleTaxExemptionFieldChange} />
                        <label className="form-check-label mb-0" htmlFor="taxExemptionAttested">
                          <span className="text-danger me-1">*</span>
                          I certify that this information is accurate, that the organization is authorized to claim exemption, and that Finbase may require supporting documentation before approval.
                        </label>
                      </div>
                    </div>
                  </div>
                </div>
                <div className="modal-footer">
                  <div className="me-auto text-muted small"><span className="text-danger">*</span> Required Field</div>
                  <button type="button" className="btn btn-outline-secondary" onClick={() => setShowTaxExemptionModal(false)} disabled={Boolean(actionLoading)}>Cancel</button>
                  <button
                    type="button"
                    className="btn btn-primary"
                    onClick={async () => {
                      const submitted = await handleSubmitTaxExemptionRequest();
                      if (submitted) setShowTaxExemptionModal(false);
                    }}
                    disabled={Boolean(actionLoading) || !canSubmitTaxExemptionForm}
                  >
                    {actionLoading === 'tax-exemption-submit' ? 'Submitting...' : 'Submit Request'}
                  </button>
                </div>
              </div>
            </div>
          </div>
          <div className="modal-backdrop fade show" />
        </>
      )}
      {pendingTaxExemptionWithdrawal && (
        <>
          <div className="modal fade show d-block" tabIndex="-1" role="dialog" aria-modal="true">
            <div className="modal-dialog modal-dialog-centered" role="document">
              <div className="modal-content">
                <div className="modal-header">
                  <h5 className="modal-title">
                    {pendingTaxExemptionWithdrawal.status === 'approved' ? 'Remove Approved Tax Exemption' : 'Undo Tax Exemption Request'}
                  </h5>
                  <button type="button" className="btn-close" aria-label="Close" onClick={() => setPendingTaxExemptionWithdrawal(null)} disabled={Boolean(actionLoading)} />
                </div>
                <div className="modal-body">
                  <div className="text-muted">
                    {pendingTaxExemptionWithdrawal.status === 'approved'
                      ? `This will remove the approved tax exemption for ${pendingTaxExemptionWithdrawal.jurisdiction_state}. If that jurisdiction matches the current billing state, future purchases will no longer be tax exempt.`
                      : `This will withdraw the pending tax exemption request for ${pendingTaxExemptionWithdrawal.jurisdiction_state}.`}
                  </div>
                </div>
                <div className="modal-footer">
                  <button type="button" className="btn btn-outline-secondary" onClick={() => setPendingTaxExemptionWithdrawal(null)} disabled={Boolean(actionLoading)}>Cancel</button>
                  <button
                    type="button"
                    className="btn btn-danger"
                    onClick={() => handleWithdrawTaxExemptionRequest(pendingTaxExemptionWithdrawal)}
                    disabled={Boolean(actionLoading)}
                  >
                    {actionLoading === `tax-exemption-withdraw-${pendingTaxExemptionWithdrawal.id}` ? 'Updating...' : 'Confirm'}
                  </button>
                </div>
              </div>
            </div>
          </div>
          <div className="modal-backdrop fade show" />
        </>
      )}
    </div>
  );
};

export default Subscription;
